import * as Popover from '@radix-ui/react-popover';
import { Box, SxProps, Theme, Divider as MuiDivider } from '@mui/material';
import { icons } from 'lucide-react';
import { forwardRef } from 'react';
import { Surface } from './Surface';
import { Toolbar } from './Toolbar';

export const Trigger = Popover.Trigger;
export const Portal = Popover.Portal;

export type MenuProps = {
  children: React.ReactNode;
  trigger: React.ReactNode;
  triggerClassName?: string;
  customTrigger?: boolean;
  isOpen?: boolean;
  onOpenChange?: (state: boolean) => void;
  withPortal?: boolean;
  tooltip?: string;
  isActive?: boolean;
};

export const Menu = ({
  customTrigger,
  trigger,
  triggerClassName,
  children,
  isOpen,
  withPortal,
  tooltip,
  onOpenChange,
}: MenuProps) => {
  return (
    <Popover.Root onOpenChange={onOpenChange}>
      {customTrigger ? (
        <Trigger asChild>{trigger}</Trigger>
      ) : (
        <Trigger asChild>
          <Toolbar.Button
            sx={triggerClassName ? { custom: triggerClassName } : undefined}
            tooltip={!isOpen ? tooltip : ''}
          >
            {trigger}
          </Toolbar.Button>
        </Trigger>
      )}
      {withPortal ? (
        // @ts-ignore
        <Popover.Portal className='z-9999'>
          <Popover.Content asChild sideOffset={8}>
            <Surface
              sx={{
                minWidth: '15rem',
                p: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                maxHeight: '20rem',
                overflow: 'auto',
                zIndex: 9999,
              }}
            >
              {children}
            </Surface>
          </Popover.Content>
        </Popover.Portal>
      ) : (
        <Popover.Content asChild sideOffset={8}>
          <Surface
            sx={{
              minWidth: '15rem',
              p: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              maxHeight: '20rem',
              overflow: 'auto',
              zIndex: 9999,
            }}
          >
            {children}
          </Surface>
        </Popover.Content>
      )}
    </Popover.Root>
  );
};

Menu.displayName = 'Menu';

export const Item = ({
  label,
  close = true,
  icon,
  iconComponent,
  disabled,
  onClick,
  isActive,
}: {
  label: string | React.ReactNode;
  icon?: keyof typeof icons;
  iconComponent?: React.ReactNode;
  close?: boolean;
  disabled?: boolean;
  onClick: () => void;
  isActive?: boolean;
}) => {
  const IconComponent = icon ? icons[icon] : null;
  const IconCustomComponent = iconComponent || null;

  const ItemComponent = close ? Popover.Close : 'button';

  return (
    <ItemComponent
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '6px',
        fontSize: '0.875rem',
        fontWeight: 500,
        textAlign: 'left',
        backgroundColor:
          isActive && !disabled ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
        width: '100%',
        borderRadius: '4px',
        border: 'none',
        color: disabled
          ? 'rgba(0, 0, 0, 0.38)'
          : isActive
            ? 'rgba(0, 0, 0, 0.87)'
            : 'rgba(0, 0, 0, 0.6)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {IconComponent && (
        <Box component={IconComponent} sx={{ width: 16, height: 16 }} />
      )}
      {IconCustomComponent}
      {label}
    </ItemComponent>
  );
};

export type CategoryTitle = {
  children: React.ReactNode;
};

export const CategoryTitle = ({ children }: CategoryTitle) => {
  return (
    <Box
      sx={{
        mt: 2,
        mb: 0.75,
        px: 0.5,
        fontSize: '0.625rem',
        fontWeight: 500,
        color: 'text.secondary',
        textTransform: 'uppercase',
        userSelect: 'none',
        '&:first-of-type': {
          mt: 0.75,
        },
      }}
    >
      {children}
    </Box>
  );
};

export const MenuDivider = () => {
  return (
    <MuiDivider
      sx={{
        my: 0.5,
        borderColor: 'divider',
      }}
    />
  );
};

MenuDivider.displayName = 'MenuDivider';
